import { useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { Input } from "antd";
import {
  PatientPopulation,
  SubActivity,
  DifferentWorkflow,
  RepeatInCertainCase,
  ContinuouslyMonitored,
  MultipleModalities,
  FreeText,
} from "..";
import { useFootnoteHooks } from "src/DigitalProtocolModule/hooks";
import {
  FOOTNOTE_GROUP,
  FOOTNOTE_TYPE_CHECK,
} from "src/DigitalProtocolModule/constants";
import styles from "./FootnoteDetails.module.scss";
import "./FootnoteDetails.scss";

const { TextArea } = Input;

interface FootnoteDetailsProps {
  currentFootnoteType: DPFootnote.FootnoteType;
  activity?: Digital_Protocol_SoA_Table_Data.Entity;
  jsonData: any;
  footnoteComponentType: string;
  footnoteGroupName: string;
  visitNumber?: number;
  period?: Digital_Protocol_SoA_Table_Data.PeriodData;
  handleJsonDataChange: (
    field: string,
    value: number | string | any[],
    previewTextPre: string,
    previewTextValue: string
  ) => void;
}

/*
  Component: Details main page
*/
export const FootnoteDetails = forwardRef(
  (
    {
      currentFootnoteType,
      activity,
      jsonData,
      footnoteComponentType,
      footnoteGroupName,
      visitNumber,
      period,
      handleJsonDataChange,
    }: FootnoteDetailsProps,
    ref: any
  ) => {
    const { filterDropdownOptionsByFootnoteType } = useFootnoteHooks();

    const detailsRef = useRef<any>();
    useImperativeHandle(ref, () => detailsRef.current);

    // get all the dropdown options by footnote type
    const configOptions = filterDropdownOptionsByFootnoteType(
      currentFootnoteType.footnote_type_id
    );

    const name = useMemo(() => {
      switch (footnoteGroupName) {
        case FOOTNOTE_GROUP.ACTIVITY:
        case FOOTNOTE_GROUP.CELL:
          return activity?.["Standard Activity"];
        case FOOTNOTE_GROUP.VISIT:
          return `Visit ${visitNumber}`;
        case FOOTNOTE_GROUP.PERIOD:
          return period?.period_name || "";
        default:
          break;
      }
    }, [activity, footnoteGroupName, visitNumber]);

    return (
      <div className="foot-note-details">
        <div>
          {footnoteComponentType ===
          FOOTNOTE_TYPE_CHECK.IS_PATIENT_POPULATION ? (
            <PatientPopulation
              ref={detailsRef}
              name={name}
              jsonData={jsonData}
              handleJsonDataChange={handleJsonDataChange}
            />
          ) : footnoteComponentType === FOOTNOTE_TYPE_CHECK.IS_SUB_ACTIVITY ? (
            <SubActivity
              ref={detailsRef}
              name={name}
              jsonData={jsonData}
              footnoteGroupName={footnoteGroupName}
              handleJsonDataChange={handleJsonDataChange}
            />
          ) : footnoteComponentType ===
            FOOTNOTE_TYPE_CHECK.IS_DIFFERENT_WORKFLOW ? (
            <DifferentWorkflow
              ref={detailsRef}
              jsonData={jsonData}
              configOptions={configOptions}
              handleJsonDataChange={handleJsonDataChange}
            />
          ) : footnoteComponentType ===
            FOOTNOTE_TYPE_CHECK.IS_REPEAT_IN_CERTAIN_CASE ? (
            <RepeatInCertainCase
              ref={detailsRef}
              name={name}
              jsonData={jsonData}
              configOptions={configOptions}
              handleJsonDataChange={handleJsonDataChange}
            />
          ) : footnoteComponentType ===
            FOOTNOTE_TYPE_CHECK.IS_CONTINUOUSLY_MONITORED ? (
            <ContinuouslyMonitored
              ref={detailsRef}
              name={name}
              jsonData={jsonData}
              configOptions={configOptions}
              handleJsonDataChange={handleJsonDataChange}
            />
          ) : footnoteComponentType ===
            FOOTNOTE_TYPE_CHECK.IS_MULTIPLE_MODALITIES ? (
            <MultipleModalities
              ref={detailsRef}
              name={name}
              activity={activity}
              jsonData={jsonData}
              configOptions={configOptions}
              handleJsonDataChange={handleJsonDataChange}
            />
          ) : footnoteComponentType === FOOTNOTE_TYPE_CHECK.IS_FREE_TEXT ? (
            <FreeText
              ref={detailsRef}
              jsonData={jsonData}
              handleJsonDataChange={handleJsonDataChange}
            />
          ) : (
            <></>
          )}
        </div>

        {(footnoteComponentType === FOOTNOTE_TYPE_CHECK.IS_PATIENT_POPULATION ||
          footnoteComponentType === FOOTNOTE_TYPE_CHECK.IS_SUB_ACTIVITY ||
          footnoteComponentType === FOOTNOTE_TYPE_CHECK.IS_DIFFERENT_WORKFLOW ||
          footnoteComponentType ===
            FOOTNOTE_TYPE_CHECK.IS_REPEAT_IN_CERTAIN_CASE ||
          footnoteComponentType ===
            FOOTNOTE_TYPE_CHECK.IS_CONTINUOUSLY_MONITORED) && (
          <div className={styles.additionalText}>
            <label style={{ fontSize: 14 }}>
              Any additional free text to add to footnote
            </label>
            <TextArea
              value={jsonData.add_free_text}
              onChange={(e) => {
                const previewTextValue = e.target.value.trim()
                  ? `${jsonData.previewPre || ""} ${e.target.value.trim()}`
                  : jsonData.previewPre || "";
                handleJsonDataChange(
                  "add_free_text",
                  e.target.value,
                  jsonData.previewPre,
                  previewTextValue || ""
                );
              }}
              maxLength={50}
              autoSize={{ minRows: 2, maxRows: 2 }}
              showCount
            />
          </div>
        )}
      </div>
    );
  }
);
